import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal, unstable_batchedUpdates } from "react-dom";
import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  MouseSensor,
  TouchSensor,
  Modifiers,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  AnimateLayoutChanges,
  SortableContext,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Item } from "./Item";
import { Container, ContainerProps } from "./Container";

const animateLayoutChanges: AnimateLayoutChanges = (args) => {
  return defaultAnimateLayoutChanges({ ...args, wasDragging: true });
};

function DroppableContainer({
  children,
  disabled,
  id,
  items,
  style,
  ...props
}: ContainerProps & {
  disabled?: boolean;
  id: UniqueIdentifier;
  items: UniqueIdentifier[];
  style?: React.CSSProperties;
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transition,
    transform,
  } = useSortable({
    id,
    data: {
      type: "container",
      children: items,
    },
    animateLayoutChanges,
  });

  return (
    <Container
      ref={disabled ? undefined : setNodeRef}
      style={{
        ...style,
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
      }}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      {...props}
    >
      {children}
    </Container>
  );
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

interface Props {
  items?: Items;
  disabled?: boolean;
  modifiers?: Modifiers;
  renderFolders: (children: React.ReactNode) => React.ReactNode;
  renderProjects?: (children: React.ReactNode) => React.ReactNode;
  renderContainer: (id: UniqueIdentifier, cover?: boolean) => React.ReactNode;
  renderItem: (id: UniqueIdentifier, pid?: UniqueIdentifier) => React.ReactNode;
  // 渲染拖拽时的假选项内容 需要特殊处理样式 所以单独提供一个 render函数 可无痛替换成 renderItem
  renderDragOverlayItem: (id: UniqueIdentifier, pid?: UniqueIdentifier) => React.ReactNode;
  onContainerReorder: (
    container: UniqueIdentifier,
    containers: UniqueIdentifier[]
  ) => void;
  onItemReorder: (
    item: UniqueIdentifier,
    container: UniqueIdentifier,
    items: UniqueIdentifier[]
  ) => void;
}

export const TRASH_ID = "void";
const PLACEHOLDER_ID = "placeholder";

export function MultipleContainers({
  items: initialItems,
  disabled,
  modifiers,
  renderFolders,
  renderProjects,
  renderContainer,
  renderItem,
  renderDragOverlayItem,
  onContainerReorder,
  onItemReorder,
}: Props) {
  const [items, setItems] = useState<Items>(() => initialItems);
  const [containers, setContainers] = useState(
    Object.keys(items) as UniqueIdentifier[]
  );

  useEffect(() => {
    setItems(initialItems);
    setContainers(Object.keys(initialItems));
  }, [initialItems]);
  // 当前拖拽中的选项id
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  // 当前拖拽过程中子选项正停留的父选项id（松开鼠标子选项应排序到该父选项下）
  const [hoveredContainerId, setHoveredContainerId] = useState<UniqueIdentifier | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer =
    activeId != null ? containers.includes(activeId) : false;

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) => container.id in items
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, "id");

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections;
        }

        if (overId in items) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId &&
                  containerItems.includes(container.id)
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items]
  );
  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        // 触发拖拽的移动距离 单位px
        distance: 12,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 12,
      },
    })
  );
  const findContainer = (id: UniqueIdentifier) => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].includes(id));
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        if (disabled) return;
        setActiveId(active.id);
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        if (disabled) return;

        const overId = over?.id;

        // 更新悬停状态
        if (overId && overId !== TRASH_ID && !(active.id in items)) {
          const overContainer = findContainer(overId);
          setHoveredContainerId(overContainer);
        } else {
          setHoveredContainerId(null);
        }

        if (overId == null || overId === TRASH_ID || active.id in items) {
          return;
        }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);

        if (!overContainer || !activeContainer) {
          return;
        }

        if (activeContainer !== overContainer) {
          setItems((items) => {
            const activeItems = items[activeContainer];
            const overItems = items[overContainer];
            const overIndex = overItems.indexOf(overId);
            const activeIndex = activeItems.indexOf(active.id);

            let newIndex: number;

            if (overId in items) {
              newIndex = overItems.length + 1;
            } else {
              const isBelowOverItem =
                over &&
                active.rect.current.translated &&
                active.rect.current.translated.top >
                  over.rect.top + over.rect.height;

              const modifier = isBelowOverItem ? 1 : 0;

              newIndex =
                overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            recentlyMovedToNewContainer.current = true;

            return {
              ...items,
              [activeContainer]: items[activeContainer].filter(
                (item) => item !== active.id
              ),
              [overContainer]: [
                ...items[overContainer].slice(0, newIndex),
                items[activeContainer][activeIndex],
                ...items[overContainer].slice(
                  newIndex,
                  items[overContainer].length
                ),
              ],
            };
          });
        }
      }}
      onDragEnd={({ active, over }) => {
        if (disabled) return;

        // 清理悬停状态
        setHoveredContainerId(null);

        if (active.id in items && over?.id) {
          setContainers((containers) => {
            const activeIndex = containers.indexOf(active.id);
            const overIndex = containers.indexOf(over.id);
            const newContainers = arrayMove(containers, activeIndex, overIndex);
            // 容器重新排序
            if (items[active.id]) {
              onContainerReorder(active.id, newContainers);
            }
            return newContainers;
          });
        }

        const activeContainer = findContainer(active.id);

        if (!activeContainer) {
          setActiveId(null);
          return;
        }

        const overId = over?.id;

        if (overId == null) {
          setActiveId(null);
          return;
        }

        // 没有用到
        if (overId === TRASH_ID) {
          setItems((items) => ({
            ...items,
            [activeContainer]: items[activeContainer].filter(
              (id) => id !== activeId
            ),
          }));
          setActiveId(null);
          return;
        }

        // 没有用到
        if (overId === PLACEHOLDER_ID) {
          const newContainerId = getNextContainerId();

          unstable_batchedUpdates(() => {
            setContainers((containers) => [...containers, newContainerId]);
            setItems((items) => ({
              ...items,
              [activeContainer]: items[activeContainer].filter(
                (id) => id !== activeId
              ),
              [newContainerId]: [active.id],
            }));
            setActiveId(null);
          });
          return;
        }

        const overContainer = findContainer(overId);

        if (overContainer) {
          const activeIndex = items[activeContainer].indexOf(active.id);
          const overIndex = items[overContainer].indexOf(overId);

          if (activeIndex !== overIndex) {
            setItems((items) => {
              const newItems = {
                ...items,
                [overContainer]: arrayMove(
                  items[overContainer],
                  activeIndex,
                  overIndex
                ),
              };
              onItemReorder(active.id, overContainer, newItems[overContainer]);
              return newItems;
            });
          } else {
            items[active.id] ||
              onItemReorder(active.id, overContainer, items[overContainer]);
          }
        }

        setActiveId(null);
      }}
      onDragCancel={onDragCancel}
      modifiers={modifiers}
    >
      <SortableContext
        items={[...containers, PLACEHOLDER_ID]}
        strategy={verticalListSortingStrategy}
      >
        {renderFolders(
          containers.map(
            (containerId) =>
              containerId !== "uncategorized" && (
                <DroppableContainer
                  key={containerId}
                  id={containerId}
                  value={renderContainer(containerId, containerId === hoveredContainerId)}
                  items={items[containerId]}
                >
                  <SortableContext
                    items={items[containerId]}
                    strategy={verticalListSortingStrategy}
                  >
                    {items[containerId].map((value, index) => {
                      const renderedItem = renderItem(value, containerId);
                      // 需要渲染第一个拖拽子项 否则无法触发拖入文件夹
                      if (renderedItem || index === 0) {
                        return (
                          <SortableItem
                            disabled={isSortingContainer}
                            key={value}
                            id={value}
                            value={renderedItem}
                            index={index}
                          />
                        );
                      }
                      return null;
                    })}
                  </SortableContext>
                </DroppableContainer>
              )
          )
        )}
        {renderProjects && renderProjects(
          <SortableContext
            // 允许存在一个与父容器 id 相同的子容器 用作占位
            items={[...items["uncategorized"], "uncategorized"]}
            strategy={verticalListSortingStrategy}
          >
            {items["uncategorized"].map((value, index) => {
              return (
                <SortableItem
                  disabled={isSortingContainer}
                  key={value}
                  id={value}
                  value={renderItem(value)}
                  index={index}
                />
              );
            })}
            {/** 占位容器 */}
            {items["uncategorized"].length === 0 && (
              <SortableItem
                placeholder
                key={"uncategorized"}
                id={"uncategorized"}
                value={<div className="h-16"></div>}
                index={items["uncategorized"].length}
              />
            )}
          </SortableContext>
        )}
      </SortableContext>
      {typeof document !== "undefined" &&
        createPortal(
          <DragOverlay dropAnimation={dropAnimation}>
            {activeId
              ? containers.includes(activeId)
                ? renderContainerDragOverlay(activeId, items[activeId])
                : renderSortableItemDragOverlay(activeId, findContainer(activeId))
              : null}
          </DragOverlay>,
          document.body
        )}
    </DndContext>
  );

  // 拖拽时渲染出来的假选项（项目）
  function renderSortableItemDragOverlay(value: UniqueIdentifier, containerId?: UniqueIdentifier) {
    return <Item value={renderDragOverlayItem(value, containerId === "uncategorized" ? undefined : containerId)} dragOverlay />;
  }

  // 拖拽时渲染出来的假容器（文件夹）
  function renderContainerDragOverlay(
    id: UniqueIdentifier,
    ids: UniqueIdentifier[]
  ) {
    return (
      <Container value={renderContainer(id)}>
        {ids.map((item) => (
          <Item key={item} value={renderItem(item, id)} />
        ))}
      </Container>
    );
  }

  function getNextContainerId() {
    const containerIds = Object.keys(items);
    const lastContainerId = containerIds[containerIds.length - 1];

    return String.fromCharCode(lastContainerId.charCodeAt(0) + 1);
  }
}

interface SortableItemProps {
  id: UniqueIdentifier;
  value: React.ReactNode;
  index: number;
  disabled?: boolean;
  placeholder?: boolean;
}

function SortableItem({
  disabled,
  placeholder,
  id,
  value,
  index,
}: SortableItemProps) {
  const {
    setNodeRef,
    listeners,
    isDragging,
    isSorting,
    transform,
    transition,
  } = useSortable({
    id,
  });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      value={value}
      dragging={isDragging}
      sorting={isSorting}
      index={index}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={placeholder ? {} : listeners}
    />
  );
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}
